import RichTextRenderer from 'ui/components/RichTextRenderer/RichTextRenderer';
import {
  CompareTableSubcopy,
  MembershipTierComparisonFeatureList,
  MembershipTierWithTrial,
  PLACEHOLDER,
  TRIAL,
} from '@/lib/constants/maverick';
import Image from 'next/image';
import { Icon } from 'ui/components/Icon/Icon';
import SelectTierButton from '../SelectTierButton/SelectTierButton';
import { useTranslation } from '@whoop/i18n/lang/client';
import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import { Separator } from 'ui/components/Separator/Separator';
import { getFilteredCompareTableSubcopy } from '@/lib/utils/maverickHelpers';
import { useSiteStore } from '@/store/SiteStoreProvider';
import { useShallow } from 'zustand/react/shallow';

interface FeatureListProps {
  featureList: MembershipTierComparisonFeatureList;
  formattedTierPrice: string;
  onSelect: (tierName: MembershipTierWithTrial) => void;
  isAddingToCart: boolean;
  numPlaceholdersToShow: number;
  compareTableSubcopyData: CompareTableSubcopy[];
  isUpgrade: boolean;
}

export default function FeatureList({
  featureList,
  formattedTierPrice,
  onSelect,
  isAddingToCart,
  numPlaceholdersToShow,
  compareTableSubcopyData,
  isUpgrade,
}: FeatureListProps): React.JSX.Element {
  const { t } = useTranslation('membershipTiers');
  const { isRafGiftFlow, country, region } = useSiteStore(
    useShallow((state) => ({
      isRafGiftFlow: state.isRafGiftFlow,
      country: state.country,
      region: state.region,
    })),
  );

  // Memoized UUID map for features used as keys in the feature list
  const featureKeyMap = useMemo(() => {
    return featureList.featuresCollection.items.reduce(
      (acc, feature) => {
        acc[feature.name] = uuid();
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [featureList.featuresCollection.items]);
  const placeholderKeys = useMemo(() => {
    return Array.from({ length: numPlaceholdersToShow }, () => uuid());
  }, [numPlaceholdersToShow]);

  const compareTableSubcopyDictionary = getFilteredCompareTableSubcopy({
    compareTableSubcopyData,
    tier: featureList.tier,
    isUpgrade,
    country,
    region,
  });

  const getSelectTierButtonTitle = (tier: string) => {
    if (isUpgrade) {
      return t('continue');
    } else if (isRafGiftFlow) {
      return t('giftTier', { tier });
    } else {
      return tier === TRIAL
        ? t('startWithTrial')
        : t('startWithTier', { tier });
    }
  };

  const priceText =
    featureList.tier !== TRIAL
      ? t(`${isUpgrade ? 'renewsAtTable' : 'availableAt'}`, {
          price: formattedTierPrice,
        })
      : t('1MonthFreeTrial');

  const placeholder: React.JSX.Element = (
    <div className='flex min-h-20 items-center'>
      <Icon alt='remove' size='small' variant='remove' />
    </div>
  );

  return (
    <div
      className='relative z-10 w-full items-center text-sm'
      data-testid={`feature-list-${featureList.tier}`}
      key={`feature-list-${featureList.tier}`}
    >
      {/* "Includes ...", device image, price, CTA  */}
      <div className='flex w-full flex-col items-center'>
        <div className='md:w-[80%]'>
          <div className='mb-2 flex min-h-16 w-full text-xs leading-5 md:text-sm'>
            <RichTextRenderer
              content={featureList.featureWithCta.displayName.json}
            />
          </div>
          <div className='relative h-28 w-full'>
            <Image
              src={featureList.featureWithCta.image?.url ?? ''}
              alt={featureList.featureWithCta.image?.url ?? ''}
              fill
              className='object-contain'
            />
          </div>
          <div className='flex w-full justify-center py-4 font-semibold'>
            {priceText}
          </div>
          <SelectTierButton
            title={getSelectTierButtonTitle(featureList.tier)}
            onClick={async () => await onSelect(featureList.tier)}
            className='h-12 w-full text-xs font-semibold'
            isAddingToCart={isAddingToCart}
          />
        </div>
        <Separator className='mb-6 mt-4' />
      </div>
      {/* Column of features (icons + names) for tier  */}
      {featureList.featuresCollection.items.map((feature) => {
        if (!feature.id) return null;
        const disclaimerForFeature =
          compareTableSubcopyDictionary?.[feature.id]?.subcopy;
        return (
          <li
            className='mb-11 flex h-full max-h-24 w-full flex-col items-center justify-center last:mb-0'
            key={`features-tier-${featureList.tier}-${
              featureKeyMap[feature.name]
            }`}
          >
            {/* If a feature is defined as 'placeholder' in contentful, it represents a feature in the middle of the list 
            that doesn't exist for this tier, ex: healthspan for tier ONE */}
            {feature.id === PLACEHOLDER ? (
              placeholder
            ) : (
              <>
                <Image
                  src={feature.image?.url ?? ''}
                  alt={feature.image?.url ?? ''}
                  height={24}
                  width={24}
                  className='pb-3'
                  unoptimized
                />
                <div className='desktop:w-full min-h-[70px] w-[75%] font-semibold'>
                  <RichTextRenderer content={feature.displayName.json} />
                  {disclaimerForFeature && (
                    <div className='text-xs font-normal'>
                      <RichTextRenderer content={disclaimerForFeature.json} />
                    </div>
                  )}
                </div>
              </>
            )}
          </li>
        );
      })}
      {/* Placeholder(s) at bottom of list for features not available for this tier  */}
      {placeholderKeys.map((key) => (
        <div
          className='mb-10 flex items-center justify-center last:mb-0'
          key={key}
        >
          {placeholder}
        </div>
      ))}
    </div>
  );
}
